import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * PowerTools' design constants.
 *
 * Named PowerTools in the gallery, `tools_machine` on disk. The id is the
 * folder name and a merchant's stored theme is keyed on it - `preset`,
 * `layout.family` and every `tools_machine.*` content override - so it stays
 * put; the label is the only part a merchant reads. Decor does the same thing.
 *
 * A trade counter rather than a boutique: three colours doing three jobs, one
 * condensed face shouting in capitals, and a machine face for every number on
 * the page. The half of the theme with no React in it.
 */
export const tokens: ThemeTokens = {
  // A condensed grotesque, which is what lets a heading say POWER TOOLS &
  // MACHINERY across a card without wrapping or dropping two sizes. It stops
  // at 700 - see `FAMILY_WEIGHTS` in `features/storefront/fonts.ts`, which is
  // where that is declared so the whole font request does not fail over it.
  headingFont: 'Oswald',
  // The safety yellow, and the theme's signature. Not the primary: the primary
  // is the red a merchant may repaint, while this is the hazard stripe the
  // masthead, the department bar and the footer's rules are drawn with, and it
  // belongs to the drawing the way the tracking does.
  accent: '#f6b800',
  // A hardware shop is read in numbers - torque, millimetres, voltage, weight,
  // warranty, price - and this is the face all of them are set in, so a
  // specification table lines up column to column.
  monoFont: 'Roboto Mono',
  // Condensed capitals close up on each other at size; a hair of tracking is
  // what keeps a heavy 48px heading from reading as one long word.
  headingTracking: '0.015em',
  // The whole family shouts. Every heading on the shop is set in capitals,
  // which is the single loudest thing about this theme.
  headingTransform: 'uppercase',
  // 1400px. A trade catalogue is a wide one: five products across beside a
  // filter rail is the density this theme is drawn at, and anything narrower
  // takes a column off rather than tightening the gutter.
  containerMax: '87.5rem',
  // Slightly under 1: a hardware shop is a busy page by design. Not as tight
  // as Voltic's 0.9 - these are cards with buttons in them, not spec rows.
  spaceScale: 0.95,
  // The three decks: the black promise rail, the yellow masthead and the
  // department bar under it.
  headerHeight: '9.5rem',
};

export const meta: ThemeMeta = {
  label: 'PowerTools',
  hint: 'A hardware trade counter: hazard-yellow masthead, a department bar and condensed capitals.',
  industries: ['automotive', 'repair_maintenance'],
  look: {
    /**
     * Three colours and their jobs, which is the whole palette:
     *
     * - the red is the *primary*, and so the one a merchant repaints: every
     *   button, every price drop, every sale band on the home page;
     * - the ink is text, and the ground of the rail, the department bar and
     *   the footer, which this theme inverts rather than tints;
     * - the yellow is the theme's own `accent` in `tokens`, not a merchant
     *   setting - see the note there.
     */
    colors: {
      primary: '#d92b1f',
      background: '#ffffff',
      surface: '#f1f2f4',
      text: '#17181b',
      muted: '#6c7076',
    },
    typography: {
      font_family: 'Barlow',
      heading_font: 'Oswald',
      heading_weight: '700',
    },
    // Square corners throughout. Nothing in a workshop has a radius on it, and
    // a rounded button under a condensed capital heading reads as a different
    // shop's button.
    buttons: { radius_px: 0, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`. A hardware
    // shop is part of its identity - drills, spanners and hard hats - and none
    // of the six industry shops sells any of that.
  },
};
